/**
 * Iter Feb 2026 · CheckInMarkerRow
 *
 * Renders the two pairs of check-in / check-out markers (blue = App
 * Acquilix, purple = Device) as absolute pins on top of a Daily-Plan
 * swimlane row. Also draws two dashed vertical lines (in / out) so the
 * eye picks up misalignment instantly.
 *
 * Positioning: caller passes `startHour` + `endHour` (defaults 7..21)
 * and the component maps every timestamp onto `left: %` inside the
 * row's grid area. The parent's grid uses `180px + N * 1fr` so the
 * timeline area starts at `180 / total * 100 %` and spans the rest.
 *
 * Props:
 *   marker              — {app_in, app_out, device_in, device_out,
 *                          discrepancy_in_minutes, discrepancy_out_minutes}
 *   startHour, endHour  — visible timeline bounds (defaults 7, 21)
 */
import { useMemo } from "react";
import { Triangle } from "lucide-react";

function hourFraction(iso, startHour, endHour) {
  if (!iso) return null;
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return null;
  const h = d.getHours() + d.getMinutes() / 60;
  if (h < startHour || h > endHour) return null;
  return (h - startHour) / (endHour - startHour);
}

function fmtHM(iso) {
  if (!iso) return "";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "";
  return `${String(d.getHours()).padStart(2, "0")}:${String(d.getMinutes()).padStart(2, "0")}`;
}

export default function CheckInMarkerRow({ marker, startHour = 7, endHour = 21 }) {
  const pins = useMemo(() => {
    if (!marker) return [];
    const out = [];
    const push = (kind, src, iso, tone, delta) => {
      const frac = hourFraction(iso, startHour, endHour);
      if (frac == null) return;
      out.push({ kind, src, iso, frac, tone, delta });
    };
    push("in",  "app",    marker.app_in,     "blue",   marker.discrepancy_in_minutes);
    push("out", "app",    marker.app_out,    "blue",   marker.discrepancy_out_minutes);
    push("in",  "device", marker.device_in,  "purple", marker.discrepancy_in_minutes);
    push("out", "device", marker.device_out, "purple", marker.discrepancy_out_minutes);
    return out;
  }, [marker, startHour, endHour]);

  if (pins.length === 0) return null;

  const flag = marker?.delta_flag || "ok";
  return (
    <div className="pointer-events-none absolute inset-0" data-testid={`daily-plan-checkin-row-${marker.user_id}`}>
      {pins.map((p, i) => {
        const isRed = flag === "red" && p.delta != null && p.delta > 15;
        const color = p.tone === "blue" ? "text-blue-600" : "text-purple-600";
        const ring = isRed ? "ring-2 ring-rose-500 ring-offset-1" : "";
        const dashCls = p.tone === "blue" ? "border-blue-300" : "border-purple-300";
        return (
          <div key={i}
               className="absolute top-0 bottom-0 pointer-events-auto"
               style={{ left: `${p.frac * 100}%`, transform: "translateX(-50%)" }}
               data-testid={`daily-plan-checkin-pin-${marker.user_id}-${p.src}-${p.kind}`}>
            <div className={`absolute top-0 bottom-0 border-l border-dashed ${dashCls}`} />
            <div className={`absolute -top-1 -translate-x-1/2 left-1/2 inline-flex items-center gap-0.5 px-1 py-0.5 bg-background border border-border shadow-sm text-[9px] font-mono tabular-nums ${color} ${ring}`}
                 title={`${p.src === "app" ? "App" : "Device"} ${p.kind === "in" ? "check-in" : "check-out"} · ${fmtHM(p.iso)}${p.delta != null ? " · Δ" + p.delta + "m" : ""}`}>
              <Triangle className={`w-2 h-2 ${p.kind === "in" ? "" : "rotate-180"} fill-current`} />
              {fmtHM(p.iso)}
            </div>
          </div>
        );
      })}
    </div>
  );
}

/**
 * "Now" vertical line — a single dashed line across all lanes for the
 * current wall-clock time. Rendered as a sibling of the swimlane grid.
 */
export function NowLine({ startHour = 7, endHour = 21 }) {
  const [now, setNow] = [new Date(), null]; // static — parent can re-render every minute
  const frac = hourFraction(now.toISOString(), startHour, endHour);
  if (frac == null) return null;
  return (
    <div className="pointer-events-none absolute top-8 bottom-0 border-l-2 border-dashed border-rose-500 z-10"
         style={{ left: `calc(180px + (100% - 180px) * ${frac})` }}
         data-testid="daily-plan-now-line">
      <span className="absolute -top-4 -translate-x-1/2 inline-flex items-center px-1 py-0.5 bg-rose-500 text-white text-[9px] font-mono">
        NOW · {fmtHM(now.toISOString())}
      </span>
    </div>
  );
}
